// sRGB → Lab conversion and CIE76 deltaE, plus input normalization.
// All pure. No imports from app code.

const HEX6_RE = /^#?([0-9a-f]{6})$/i;
const HEX3_RE = /^#([0-9a-f]{3})$/i;
const RGB_RE = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*[\d.]+\s*)?\)$/i;

function toCanonicalHex(r, g, b) {
    const hex = (n) => n.toString(16).padStart(2, '0');
    return `#${hex(r)}${hex(g)}${hex(b)}`;
}

export function normalizeColor(input) {
    if (typeof input !== 'string') return null;
    const trimmed = input.trim();
    if (!trimmed) return null;

    const m6 = trimmed.match(HEX6_RE);
    if (m6) {
        return `#${m6[1].toLowerCase()}`;
    }
    const m3 = trimmed.match(HEX3_RE);
    if (m3) {
        const [a, b, c] = m3[1].toLowerCase();
        return `#${a}${a}${b}${b}${c}${c}`;
    }
    const mRgb = trimmed.match(RGB_RE);
    if (mRgb) {
        const r = Number(mRgb[1]);
        const g = Number(mRgb[2]);
        const b = Number(mRgb[3]);
        if (r > 255 || g > 255 || b > 255) return null;
        return toCanonicalHex(r, g, b);
    }
    return null;
}

function srgbToLinear(c) {
    const n = c / 255;
    return n <= 0.04045 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
}

function linearRgbToXyz(r, g, b) {
    return {
        X: r * 0.4124564 + g * 0.3575761 + b * 0.1804375,
        Y: r * 0.2126729 + g * 0.7151522 + b * 0.072175,
        Z: r * 0.0193339 + g * 0.119192 + b * 0.9503041,
    };
}

const D65 = { X: 0.95047, Y: 1.0, Z: 1.08883 };
function f(t) {
    const delta = 6 / 29;
    return t > Math.pow(delta, 3) ? Math.cbrt(t) : t / (3 * delta * delta) + 4 / 29;
}

export function hexToLab(input) {
    const hex = normalizeColor(input);
    if (!hex) throw new Error(`hexToLab: cannot parse "${input}"`);
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const rl = srgbToLinear(r);
    const gl = srgbToLinear(g);
    const bl = srgbToLinear(b);
    const { X, Y, Z } = linearRgbToXyz(rl, gl, bl);
    const fx = f(X / D65.X);
    const fy = f(Y / D65.Y);
    const fz = f(Z / D65.Z);
    return {
        L: 116 * fy - 16,
        a: 500 * (fx - fy),
        b: 200 * (fy - fz),
    };
}

export function deltaE(labA, labB) {
    const dL = labA.L - labB.L;
    const da = labA.a - labB.a;
    const db = labA.b - labB.b;
    return Math.sqrt(dL * dL + da * da + db * db);
}
